
// Mirrors TIER_FEES in functions/src/lib/platform-fees.ts — keep in sync.
// `fixedAud` applies to one-time sales and the first subscription charge;
// `recurringFixedAud` (reduced) applies to every subscription renewal after the
// first. The percentage is identical for both.
export const TIER_FEES: Record<string, { percentage: number; fixedAud: number; recurringFixedAud: number }> = {
  Bronze:   { percentage: 0.045,  fixedAud: 2,    recurringFixedAud: 1.50 },
  Silver:   { percentage: 0.0425, fixedAud: 1.75, recurringFixedAud: 1.25 },
  Gold:     { percentage: 0.04,   fixedAud: 1.50, recurringFixedAud: 1 },
  Platinum: { percentage: 0.0375, fixedAud: 1.25, recurringFixedAud: 0.75 },
  Diamond:  { percentage: 0.035,  fixedAud: 1,    recurringFixedAud: 0.50 },
  // Founder: invite-only founding-seller tier. 3.5% + A$0.75 (below Diamond's
  // fixed fee) with no subscription. Kept last: src/app/fees/page.tsx lists the
  // tiers in this order and Founder is not a rung, so it follows Diamond there
  // (listed since 2026-10-15, owner). Raised from A$0.50 on 2026-10-05 (owner).
  // Renewals are A$0.25 because /fees promises every renewal's fixed component
  // falls by A$0.50.
  Founder:  { percentage: 0.035,  fixedAud: 0.75, recurringFixedAud: 0.25 },
};

/**
 * The fixed part of the fee is capped at this percentage of the line's price
 * (owner decision 2026-10-05). MIRRORS functions/src/lib/platform-fees.ts,
 * which books the sale; this copy drives the figures sellers are shown.
 */
export const FIXED_FEE_CAP_PERCENT = 10;

/**
 * The cap never cuts the fixed part below this. It only limits the cap: a
 * tier's own lower fixed amount (a Founder renewal) is kept, never raised.
 */
export const FIXED_FEE_MIN_AUD = 0.5;

/**
 * The fixed component of the platform fee on one line, in AUD cents: the
 * tier's fixed amount (the reduced one on a renewal), cut to
 * FIXED_FEE_CAP_PERCENT of the price but not below FIXED_FEE_MIN_AUD. Rounds
 * the cap down, so it never exceeds 10% of the price. Mirror of the server's
 * calcFixedFeeMinor; the two must stay identical.
 *
 * @param priceAud The line's price in AUD.
 * @param tier The seller's tier (unknown tiers resolve as Bronze).
 * @param recurring A subscription renewal after the first charge.
 * @returns The fixed component in AUD cents. Zero for a non-positive price.
 */
export function calcFixedFeeMinor(priceAud: number, tier: string, recurring = false): number {
  if (!Number.isFinite(priceAud) || priceAud <= 0) return 0;
  const { fixedAud, recurringFixedAud } = TIER_FEES[tier] ?? TIER_FEES.Bronze;
  const listedMinor = Math.round((recurring ? recurringFixedAud : fixedAud) * 100);
  const priceMinor = Math.round(priceAud * 100);
  const capMinor = Math.max(
    Math.round(FIXED_FEE_MIN_AUD * 100),
    Math.floor((priceMinor * FIXED_FEE_CAP_PERCENT) / 100),
  );
  return Math.min(listedMinor, capMinor);
}

/**
 * The currencies supa.place settles and holds, and therefore the currencies in
 * which no conversion cost arises on either leg.
 *
 * AUD ONLY, and that is a fact about the processor rather than a choice.
 * Stripe confirmed 2026-08-07 that an Australian account can settle USD and NZD
 * only into an Australian-domiciled account in that currency, and that
 * "virtual bank accounts like Wise or Airwallex" are not accepted. Since the
 * settlement destination is an Airwallex Global Account, USD and NZD settlement
 * is unavailable, so every non-AUD charge is converted to AUD by the processor
 * on arrival and every non-AUD payout is converted on the way out. Both costs
 * are real, so both are recovered.
 *
 * MIRRORS `PLATFORM_HELD_CURRENCIES` in functions/src/lib/platform-fees.ts.
 * THIS IS A PUBLISHED PRICE INPUT, NOT A CONFIG VALUE: both currency fees key
 * off it, and it is hard-coded rather than read from the SETTLEMENT_CURRENCIES
 * deployment parameter so that an ops change can never silently change what a
 * buyer or a seller is charged. Changing it is a pricing change and must be
 * made in both copies, in the /fees disclosure, and in SETTLEMENT_CURRENCIES.
 */
export const PLATFORM_HELD_CURRENCIES: readonly string[] = ['AUD'];

const HELD_CURRENCY_SET = new Set<string>(PLATFORM_HELD_CURRENCIES);

/**
 * Whether money in this currency moves without a conversion on our side.
 *
 * @param {unknown} currency ISO 4217 code, any case.
 * @return {boolean} True when supa.place holds this currency.
 */
export function isHeldCurrency(currency: unknown): boolean {
  return typeof currency === 'string' && HELD_CURRENCY_SET.has(currency.trim().toUpperCase());
}

/** Buyer-side currency conversion surcharge, as a fraction of the pre-tax order. */
export const BUYER_FX_SURCHARGE_RATE = 0.02;

/**
 * Seller-side payout conversion component, added to the platform fee percentage
 * when the seller is paid out in a currency we do not hold.
 *
 * TIERED, because the underlying cost is tiered. Airwallex charges roughly 0.5%
 * over interbank on its eleven major currencies and roughly 1% on everything
 * else, so a flat 0.5% would have quietly lost money on every seller paid in a
 * minor currency. Both figures are pass-throughs matched to that cost.
 *
 * Independent of the buyer-side surcharge: the two have different triggers
 * (which currency the buyer paid in, versus which currency the seller is paid
 * in) and a single order can incur either, both or neither.
 */
export const PAYOUT_FX_MAJOR_RATE = 0.005;
export const PAYOUT_FX_MINOR_RATE = 0.01;

/**
 * The currencies our payout provider converts at the cheaper major-currency
 * rate. Everything outside this list, and outside PLATFORM_HELD_CURRENCIES,
 * costs about twice as much to convert into.
 *
 * AUD is on this list but is charged NOTHING, because it is held and no
 * conversion happens at all. The list therefore decides the rate for the other
 * ten: CAD, CHF, CNY, EUR, GBP, HKD, JPY, NZD, SGD and USD. (CHF stays as a price
 * but no seller is paid in it since 2026-10-07: there is no local CHF route, so
 * Switzerland is paid in EUR. Copy that lists payout currencies must filter with
 * isSupportedPayoutCurrency. See seller-countries.ts.)
 */
export const PAYOUT_FX_MAJOR_CURRENCIES: readonly string[] = [
  'AUD', 'CAD', 'CHF', 'CNY', 'EUR', 'GBP', 'HKD', 'JPY', 'NZD', 'SGD', 'USD',
];

/**
 * The remaining currencies a seller can be paid in, which our provider converts
 * at roughly twice the major rate.
 *
 * Listed EXPLICITLY rather than treated as "everything not major". An
 * open-ended rule bills any unrecognised string at the higher rate, which means
 * a typo, a retired code or a future currency added upstream would silently
 * overcharge a seller. Together with PAYOUT_FX_MAJOR_CURRENCIES this is the
 * complete published price table.
 */
export const PAYOUT_FX_MINOR_CURRENCIES: readonly string[] = [
  'AED', 'ARS', 'BDT', 'BOB', 'BRL', 'BWP', 'CLP', 'COP', 'CZK', 'DKK',
  'GMD', 'HUF', 'IDR', 'ILS', 'INR', 'KES', 'KRW', 'LKR', 'LSL', 'MAD',
  'MGA', 'MWK', 'MXN', 'MYR', 'NAD', 'NGN', 'NOK', 'NPR', 'PEN', 'PHP',
  'PKR', 'PLN', 'PYG', 'RON', 'RWF', 'SEK', 'THB', 'TRY', 'UYU', 'VND',
  'XAF', 'XOF', 'ZAR', 'ZMW',
];

const PAYOUT_FX_MAJOR_SET = new Set<string>(PAYOUT_FX_MAJOR_CURRENCIES);
const PAYOUT_FX_MINOR_SET = new Set<string>(PAYOUT_FX_MINOR_CURRENCIES);

/**
 * The payout-conversion rate for a seller paid in `currency`.
 *
 * Three tiers, from two explicit lists: nothing for a currency we hold (no
 * conversion occurs), the major rate for one our provider converts cheaply, the
 * minor rate for the rest of the payout currencies. Anything unrecognised
 * returns 0.
 *
 * @param {unknown} currency The seller's payout currency, ISO 4217.
 * @return {number} Fraction to add to the platform fee percentage.
 */
export function payoutFxRate(currency: unknown): number {
  if (typeof currency !== "string") return 0;
  const code = currency.trim().toUpperCase();
  if (!code) return 0;
  if (HELD_CURRENCY_SET.has(code)) return 0;
  if (PAYOUT_FX_MAJOR_SET.has(code)) return PAYOUT_FX_MAJOR_RATE;
  if (PAYOUT_FX_MINOR_SET.has(code)) return PAYOUT_FX_MINOR_RATE;
  // Not a currency we recognise. Charge nothing rather than assume the higher
  // rate: a typo or an unexpected code must never silently overcharge a seller.
  return 0;
}

// Monthly cost of the Diamond Subscription per base seller tier (AUD).
export const DIAMOND_COSTS_AUD: Record<'Bronze' | 'Silver' | 'Gold' | 'Platinum', number> = {
  Bronze:   50,
  Silver:   45,
  Gold:     40,
  Platinum: 35,
};

// Plain-text formatters shared by user-facing copy (FAQ answers, chatbot
// system prompt, etc.) so wording stays in sync with TIER_FEES.
// Percentage with up to 2 decimals, trailing zeros dropped (4.25%, 3.5%, 4%).
// Round first: float dust makes 0.04 * 100 !== 4, so Number.isInteger is unsafe.
const fmtPct = (p: number) => `${Math.round(p * 10000) / 100}%`;
// Tier fees: always 2 decimals so the fee disclosure reads consistently.
export const fmtAudFixed = (n: number) => `A$${n.toFixed(2)}`;
// Subscription prices: drop trailing zeros on whole-dollar amounts.
export const fmtAudCompact = (n: number) =>
  Number.isInteger(n) ? `A$${n}` : `A$${n.toFixed(2)}`;

/**
 * The fixed-fee cap in one sentence, for every surface that quotes the tier
 * fees (/fees, /sell, /help, the home FAQ, the support bot), so none of them
 * can state it differently. Built from the constants and calcFixedFeeMinor, so
 * the example is the figure a sale is actually charged.
 */
export const FIXED_FEE_CAP_NOTE =
  `On a low-priced item the fixed amount is cut to ${FIXED_FEE_CAP_PERCENT}% of the price, ` +
  `but not below ${fmtAudFixed(FIXED_FEE_MIN_AUD)}: an A$10 item at Bronze pays ` +
  `${fmtAudFixed(calcFixedFeeMinor(10, 'Bronze') / 100)} instead of ${fmtAudFixed(TIER_FEES.Bronze.fixedAud)}.`;

export function formatTierFee(tier: keyof typeof TIER_FEES | string): string {
  const fee = TIER_FEES[tier] ?? TIER_FEES.Bronze;
  return `${fmtPct(fee.percentage)} + ${fmtAudFixed(fee.fixedAud)}`;
}

/**
 * The tier fee as it actually reads for a seller paid out in `currency`: the
 * payout-conversion component folded into the percentage, with the fixed amount
 * unchanged (the component rides the percentage only).
 *
 * For seller-facing copy that has to state the rate MOST sellers pay. AUD is the
 * only payout currency without a component, so formatTierFee alone understates
 * the fee for nearly every seller.
 *
 * @param {string} tier The seller's tier.
 * @param {string} currency The seller's payout currency, ISO 4217.
 * @return {string} e.g. "5% + A$2.00".
 */
export function formatTierFeeForPayoutCurrency(
  tier: keyof typeof TIER_FEES | string,
  currency: string,
): string {
  const fee = TIER_FEES[tier] ?? TIER_FEES.Bronze;
  // Round the sum before formatting: 0.045 + 0.005 is 0.049999999999999996 in
  // binary floating point, which would render as 5% only by luck of the rounding
  // inside fmtPct. Doing it here keeps the published figure exact.
  const pct = Math.round((fee.percentage + payoutFxRate(currency)) * 10000) / 10000;
  return `${fmtPct(pct)} + ${fmtAudFixed(fee.fixedAud)}`;
}

/**
 * The reduced fee applied to subscription RENEWALS (every charge after the
 * first). Same percentage as the tier's headline fee, with a lower fixed
 * component. The first charge of a subscription uses formatTierFee.
 */
export function formatTierRenewalFee(tier: keyof typeof TIER_FEES | string): string {
  const fee = TIER_FEES[tier] ?? TIER_FEES.Bronze;
  return `${fmtPct(fee.percentage)} + ${fmtAudFixed(fee.recurringFixedAud)}`;
}

export function formatDiamondCost(tier: keyof typeof DIAMOND_COSTS_AUD): string {
  return fmtAudCompact(DIAMOND_COSTS_AUD[tier]);
}

export interface PlatformFeeOptions {
  /**
   * A recurring subscription charge AFTER the initial one. The full fixed
   * per-transaction component (`fixedAud`) is charged ONCE, on the first charge
   * of a subscription (exactly like a one-time sale); every renewal after that
   * uses the reduced `recurringFixedAud`. Defaults to false, so one-time sales
   * and the first subscription charge keep the full `percentage + fixedAud` fee.
   */
  recurring?: boolean;
  /**
   * The SELLER's payout currency at the time of the sale, which decides the
   * payout-conversion component via payoutFxRate(). Never the buyer's currency.
   */
  payoutCurrency?: string;
}

/**
 * Returns the platform fee in AUD for a product priced in AUD.
 * Uses the product/seller tier to select the fee structure.
 *
 * For subscriptions, pass `{ recurring: true }` on every charge after the
 * first so the reduced renewal fixed fee applies; the percentage is unchanged.
 */
export function calcPlatformFee(
  priceAud: number,
  tier: string,
  opts: PlatformFeeOptions = {},
): number {
  // NO CONSIDERATION, NO FEE. The fixed component does not scale with price, so on
  // a A$0 line it was levied in full and a free listing charged the seller for
  // giving something away. Mirrors functions/src/lib/platform-fees.ts, which is
  // the copy that actually books the sale; this one drives the fee shown in the
  // dashboard, so it has to agree or the seller is quoted a fee they never pay.
  if (!Number.isFinite(priceAud) || priceAud <= 0) return 0;
  const { percentage } = TIER_FEES[tier] ?? TIER_FEES.Bronze;
  // The payout-conversion component rides on the PERCENTAGE, not the fixed fee,
  // because the cost it recovers is itself a percentage of the amount converted.
  //
  // Rounded to four decimals because adding the two rates in binary floating
  // point does not land on the figure we publish: 0.035 + 0.01 is
  // 0.045000000000000005, which turns a A$6.00 fee into A$6.000000000000001 and
  // leaks a sub-cent into every downstream sum. Every rate in the table is exact
  // at four decimals, so this rounds to the published number, never away from it.
  const rate = Math.round((percentage + payoutFxRate(opts.payoutCurrency)) * 10000) / 10000;
  return priceAud * rate + calcFixedFeeMinor(priceAud, tier, opts.recurring) / 100;
}

/**
 * The payout-conversion COMPONENT of the fee above, in AUD, on its own.
 *
 * Mirror of the functions-side calcSellerConversionFee; the two must stay
 * identical, because the server figure is what the ledger books and this one is
 * what the seller is shown. Both are taken at the time of the sale, and nothing
 * further is deducted at withdrawal.
 *
 * The component is a COMPONENT: it is already inside what calcPlatformFee
 * returns, so never add the two together.
 *
 * @param priceAud The item's gross price in AUD.
 * @param payoutCurrency The SELLER's payout currency at the sale.
 * @returns The conversion component in AUD. Zero for a held currency.
 */
export function calcSellerConversionFee(priceAud: number, payoutCurrency?: string): number {
  if (!Number.isFinite(priceAud) || priceAud <= 0) return 0;
  return priceAud * payoutFxRate(payoutCurrency);
}

/**
 * The buyer-side currency conversion surcharge for one order, in AUD.
 *
 * Charged on the PRE-TAX consideration and added to the taxable base, so
 * consumption tax is calculated on the amount the buyer actually pays.
 *
 * @param {number} preTaxAud Subtotal plus any processing fee, in AUD.
 * @param {unknown} presentmentCurrency The currency the card will be charged in.
 * @return {number} The surcharge in AUD, rounded to cents. Zero when held.
 */
export function calcBuyerFxSurcharge(preTaxAud: number, presentmentCurrency: unknown): number {
  if (!Number.isFinite(preTaxAud) || preTaxAud <= 0) return 0;
  if (isHeldCurrency(presentmentCurrency)) return 0;
  return Math.round(preTaxAud * BUYER_FX_SURCHARGE_RATE * 100) / 100;
}
